import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { api } from 'src/common/utils/api';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ChatFile } from '../../chats/types';
import { CHAT_SKILLS_QUERY_KEY } from '../../chats/useChatSkills';
import {
  ChatMessage,
  ChatMode,
  ChatState,
  EmailDraft,
  ErrorMarker,
  FinalChunkData,
  isChatMessage,
  Message,
  MessageAttachment,
  MessageSkill,
  MessageContent,
  MessageDirection,
  MessageState,
  OutlookConnectRequest,
  PresentedFile,
  ProposedAction,
  ProposedActionStatus,
  RetryPayload,
  ScenarioUpdate,
  StoredMessage,
} from '../types';
import { mergeScenarioUpdate } from './mergeScenarioUpdate';
import { StreamOutcome, useChatTransport } from './useChatTransport';

type StateChangeCallback = (state: ChatState, data?: FinalChunkData) => void;

type ClientChatOptions = {
  mode?: typeof ChatMode.CLIENT;
  onStateChange?: StateChangeCallback;
};

type DocumentChatOptions = {
  mode: typeof ChatMode.DOCUMENT;
  entityId: string;
  requestBodyBuilder: (
    message: string,
    conversationId: string,
    verify?: { sectionPath?: string[] },
  ) => object;
  onStateChange?: StateChangeCallback;
};

type FamilyChatOptions = {
  mode: typeof ChatMode.FAMILY;
  familyId: string;
  // The member the advisor is currently viewing; bare questions default to them. Null/omitted on
  // the whole-household view.
  focusedClientId?: string | null;
  onStateChange?: StateChangeCallback;
};

type UseChatMessagesOptions = ClientChatOptions | DocumentChatOptions | FamilyChatOptions;

type ModeConfig = {
  handledStates: ChatState[];
  fireTransitionCallbacks: boolean;
};

const MODE_CONFIGS: Record<ChatMode, ModeConfig> = {
  [ChatMode.CLIENT]: {
    handledStates: [
      ChatState.THINKING,
      ChatState.ANSWERING,
      ChatState.TOOL_STARTED,
      ChatState.TOOL_FINISHED,
      ChatState.TOOL_LIMIT_REACHED,
      ChatState.FILE_PRESENTED,
      ChatState.DATA_UPDATED,
      ChatState.ACTION_PROPOSED,
      ChatState.FOLLOW_UPS_SUGGESTED,
    ],
    fireTransitionCallbacks: false,
  },
  [ChatMode.FAMILY]: {
    handledStates: [
      ChatState.THINKING,
      ChatState.ANSWERING,
      ChatState.TOOL_STARTED,
      ChatState.TOOL_FINISHED,
      ChatState.TOOL_LIMIT_REACHED,
      ChatState.FILE_PRESENTED,
      ChatState.DATA_UPDATED,
      ChatState.ACTION_PROPOSED,
      ChatState.FOLLOW_UPS_SUGGESTED,
    ],
    fireTransitionCallbacks: false,
  },
  [ChatMode.DOCUMENT]: {
    handledStates: [ChatState.THINKING, ChatState.ANSWERING, ChatState.EDITING, ChatState.FINAL],
    fireTransitionCallbacks: true,
  },
};

const createUserMessage = (
  content: string,
  attachments?: MessageAttachment[],
  skill?: MessageSkill,
): ChatMessage => {
  const response = [{ state: 'SENT', content }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id: uuidv4(),
    response,
    sender: 'Me',
    direction: MessageDirection.Outgoing,
    thinkingContent,
    answerContent,
    currentState,
    ...(attachments?.length ? { attachments } : {}),
    ...(skill ? { skill } : {}),
  };
};

const createResponseMessage = (
  content?: string,
  id?: string,
  presentedFiles?: PresentedFile[],
): ChatMessage => {
  const response = content ? [{ state: 'ANSWERING', content }] : [];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id: id || uuidv4(),
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
    ...(presentedFiles?.length ? { presentedFiles } : {}),
  };
};

const createThinkingPlaceholderMessage = (id: string): ChatMessage => {
  const response: MessageContent[] = [{ state: ChatState.THINKING, content: '' }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id,
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

// Presented files survive persistence only as markdown links folded into the
// assistant text (backend FILE_PRESENTED handling), so history load lifts
// signed sandbox-export links back out of the prose into PresentedFileCards.
// Image presentations (`![...]`) are left inline deliberately.
const SANDBOX_EXPORT_LINK_PATTERN =
  /(?<!!)\[([^\]\n]*)\]\((https?:\/\/[^\s)"'<>]*\bsandbox_exports\/[^\s)"'<>]*)\)/g;

const extractPresentedFiles = (
  content: string,
): { content: string; presentedFiles: PresentedFile[] } => {
  const presentedFiles: PresentedFile[] = [];
  const stripped = content.replace(
    SANDBOX_EXPORT_LINK_PATTERN,
    (match, description: string, url: string) => {
      if (!url.includes('X-Amz-Signature=')) return match;
      presentedFiles.push({ description, url });
      return '';
    },
  );
  if (!presentedFiles.length) return { content, presentedFiles };
  return { content: stripped.replace(/\n{3,}/g, '\n\n').trim(), presentedFiles };
};

const rawMessageToChatMessages = (raw: StoredMessage): Message[] => {
  if (typeof raw.content !== 'string') return [];
  // A slash command sent on its own has no text; the skill it picked is the whole message.
  const isCommandOnly = raw.role === 'user' && raw.skill !== undefined;
  if (!raw.content.trim() && !isCommandOnly) return [];
  if (raw.role === 'user') return [createUserMessage(raw.content, raw.attachments, raw.skill)];
  if (raw.role === 'assistant') {
    const { content, presentedFiles } = extractPresentedFiles(raw.content);
    if (!presentedFiles.length) return [createResponseMessage(raw.content)];
    return [createResponseMessage(content || undefined, undefined, presentedFiles)];
  }
  return [];
};

const findChatMessageById = (messages: Message[], id: string): ChatMessage | undefined =>
  messages.find((msg): msg is ChatMessage => msg.id === id && isChatMessage(msg));

// A message carrying widget payloads (file cards, proposals, email drafts) is never "empty",
// even with no answer text — sweeping it would silently delete the widget.
const isEmptyThinkingPlaceholder = (message: Message): boolean =>
  isChatMessage(message) &&
  message.direction === MessageDirection.Incoming &&
  message.response.length === 1 &&
  message.response[0].state === ChatState.THINKING &&
  message.response[0].content === '' &&
  !message.currentToolName &&
  !message.presentedFiles?.length &&
  !message.scenarioUpdates?.length &&
  !message.proposedActions?.length &&
  !message.emailDrafts?.length &&
  !message.outlookConnectRequests?.length &&
  message.currentState !== ChatState.TOOL_LIMIT_REACHED;

type ParsedChunk = {
  state: MessageState;
  content: string;
  data?: FinalChunkData;
};

const parseStreamChunk = (rawData: string): ParsedChunk | null => {
  try {
    const parsed = JSON.parse(rawData);
    return {
      state: parsed.state,
      content: parsed.content,
      data: parsed.data,
    };
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

const processContentTransforms = (content: string): string => {
  const transforms = [
    {
      regex: /<span\s+[^>]*style="[^"]*"[^>]*>(.*?)<\/span>/gi,
      replacement: '$1',
    },

    // Answer content is sometimes incorrectly wrapped in a <response> tag
    { regex: /<response>(.*?)<\/response>/gis, replacement: '$1' },
  ];

  return transforms.reduce(
    (transformed, { regex, replacement }) => transformed.replace(regex, replacement),
    content,
  );
};

const computeMessageProperties = (
  response: MessageContent[],
): {
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
} => {
  const thinkingContent = processContentTransforms(
    response
      .filter(r => r.state !== 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const answerContent = processContentTransforms(
    response
      .filter(r => r.state === 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const currentState = response[response.length - 1]?.state || 'SENT';

  return { thinkingContent, answerContent, currentState };
};

const appendToResponse = (
  message: ChatMessage,
  state: MessageState,
  content: string,
): ChatMessage => {
  const transformedContent = processContentTransforms(content);
  const newResponse = [...message.response];

  const lastResponse = newResponse[newResponse.length - 1];
  if (lastResponse && lastResponse.state === state) {
    newResponse[newResponse.length - 1] = {
      ...lastResponse,
      content: lastResponse.content + transformedContent,
    };
  } else {
    newResponse.push({ state, content: transformedContent });
  }

  const { thinkingContent, answerContent, currentState } = computeMessageProperties(newResponse);

  return {
    ...message,
    response: newResponse,
    thinkingContent,
    answerContent,
    currentState,
    // The retry notice is transient: once real answer content streams in, drop it.
    retryNotice: state === ChatState.ANSWERING ? undefined : message.retryNotice,
  };
};

export function useChatMessages(options?: UseChatMessagesOptions) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<Message[]>([]);
  const [latestIncomingId, setLatestIncomingId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentChatState, setCurrentChatState] = useState<ChatState | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const chatStateRef = useRef<ChatState | null>(null);
  const {
    streamRequest,
    subscribeToTurn,
    cancelTurn,
    abort: abortTransport,
    buildClientChatUrl,
    buildCrossClientChatUrl,
    buildFamilyChatUrl,
    buildDocumentEditUrl,
    buildTurnStreamUrl,
  } = useChatTransport();

  // The background turn currently streaming into the open chat, so the stop button can tell the
  // worker which turn to cancel. Null on the live/document paths, which have no cancellable turn.
  const activeTurnRef = useRef<{ conversationId: string; turnId: string } | null>(null);

  const { flagEnabledSiestaChats, flagEnabledSiestaAsyncGeneration } = useFlags();
  const backgroundGenerationEnabled = flagEnabledSiestaChats && flagEnabledSiestaAsyncGeneration;

  const markConversationRead = useCallback(
    (conversationId: string): void => {
      if (!flagEnabledSiestaChats) return;
      api
        .put(`conversations/${conversationId}/read`)
        .catch(error => Sentry.captureException(error));
    },
    [flagEnabledSiestaChats],
  );

  const mode = options?.mode ?? ChatMode.CLIENT;
  const modeConfig = MODE_CONFIGS[mode];
  const onStateChangeRef = useRef(options?.onStateChange);
  onStateChangeRef.current = options?.onStateChange;

  const modeConfigRef = useRef(modeConfig);
  modeConfigRef.current = modeConfig;

  // The document id for the in-flight verify, captured at send time. The FINAL handler runs from a
  // memoised stream callback whose `options` closure can be stale, so it can't read entityId itself.
  const verifyMeetingNoteIdRef = useRef<string | undefined>(undefined);

  const updateChatState = useCallback((newState: ChatState | null) => {
    chatStateRef.current = newState;
    setCurrentChatState(newState);
  }, []);

  const addMessage = useCallback((message: ChatMessage): void => {
    setMessages(prev => [...prev, message]);
    if (message.direction === MessageDirection.Incoming) {
      setLatestIncomingId(message.id);
    }
  }, []);

  const handleIncomingStreamChunk = useCallback(
    (messageId: string, rawData: string): void => {
      if (chatStateRef.current === null) return;

      const parsedChunk = parseStreamChunk(rawData);
      if (!parsedChunk) return;

      const { state, content, data } = parsedChunk;
      const config = modeConfigRef.current;

      if (state === ChatState.EDITING) {
        if (!config.handledStates.includes(ChatState.EDITING)) return;
        if (chatStateRef.current !== ChatState.EDITING) {
          updateChatState(ChatState.EDITING);
          onStateChangeRef.current?.(ChatState.EDITING);
        }
        return;
      }

      if (state === ChatState.FINAL) {
        if (!config.handledStates.includes(ChatState.FINAL)) return;
        updateChatState(ChatState.FINAL);
        onStateChangeRef.current?.(ChatState.FINAL, data);

        if (data?.verifyOutcome) {
          const meetingNoteId = verifyMeetingNoteIdRef.current;
          setMessages(currentMessages => {
            const target = findChatMessageById(currentMessages, messageId);
            if (!target) return currentMessages;
            const updated = {
              ...target,
              // Leave the thinking-placeholder state behind so the post-stream cleanup doesn't
              // filter this message out — VerifyResult renders off verifyOutcome, not the response.
              response: [{ state: ChatState.ANSWERING, content: '' }],
              currentState: ChatState.ANSWERING,
              verifyOutcome: data.verifyOutcome,
              evidence: data.evidence ?? [],
              verifyMeetingNoteId: meetingNoteId,
              verifyMessage: data.assistantMessage,
              verifyHeadline: data.verifyHeadline,
              verifyUnresolved: data.verifyUnresolved,
            };
            return currentMessages.map(msg => (msg.id === messageId ? updated : msg));
          });
          return;
        }

        if (data?.assistantMessage) {
          setMessages(currentMessages => {
            const targetMessage = findChatMessageById(currentMessages, messageId);
            if (!targetMessage || targetMessage.answerContent.trim()) return currentMessages;
            const updatedMessage = appendToResponse(
              targetMessage,
              ChatState.ANSWERING,
              data.assistantMessage,
            );
            return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
          });
          return;
        }

        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage || !isEmptyThinkingPlaceholder(targetMessage)) return currentMessages;
          return currentMessages.filter(msg => msg.id !== messageId);
        });
        setLatestIncomingId(prev => (prev === messageId ? null : prev));
        return;
      }

      if (state === ChatState.RETRY_NOTICE) {
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) {
            const newMessage = createResponseMessage();
            newMessage.id = messageId;
            newMessage.retryNotice = content;
            setLatestIncomingId(messageId);
            return [...currentMessages, newMessage];
          }
          return currentMessages.map(msg =>
            msg.id === messageId ? { ...target, retryNotice: content } : msg,
          );
        });
        return;
      }

      if (state === ChatState.FILE_PRESENTED) {
        let file: PresentedFile;
        try {
          file = JSON.parse(content) as PresentedFile;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? { ...target, presentedFiles: [...(target.presentedFiles ?? []), file] }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.DATA_UPDATED) {
        let update: ScenarioUpdate;
        try {
          update = JSON.parse(content) as ScenarioUpdate;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        if (update.domain === 'cashflow') {
          // Only the model Siesta touched and the list cards that summarise
          // it — not every query under the client's cashflow scope.
          const scopes: [string, string][] = [['clients', update.clientId]];
          if (update.familyId) scopes.push(['families', update.familyId]);
          const touched = (key: readonly unknown[]) =>
            key[3] === undefined ||
            typeof key[3] === 'object' ||
            (key[3] === update.scenarioId && key[4] !== 'profile-sync');
          for (const [kind, id] of scopes) {
            void queryClient.invalidateQueries({
              queryKey: [kind, id, 'cashflow-scenarios'],
              predicate: query => touched(query.queryKey),
            });
          }
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? { ...target, scenarioUpdates: mergeScenarioUpdate(target.scenarioUpdates, update) }
              : msg,
          );
        });
        return;
      }

      // Must early-return like FILE_PRESENTED: the content is a JSON payload of suggested
      // next steps, rendered as chips under the reply rather than as answer text.
      if (state === ChatState.FOLLOW_UPS_SUGGESTED) {
        let followUps: { questions?: unknown };
        try {
          followUps = JSON.parse(content) as { questions?: unknown };
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        const questions = Array.isArray(followUps.questions)
          ? followUps.questions.filter((q): q is string => typeof q === 'string' && q.length > 0)
          : [];
        if (questions.length === 0) return;
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId ? { ...target, followUps: questions } : msg,
          );
        });
        return;
      }

      // Must early-return like FILE_PRESENTED: the content is a JSON payload for the approval
      // widget, and falling through would render it as answer text.
      if (state === ChatState.ACTION_PROPOSED) {
        let proposal: ProposedAction;
        try {
          proposal = JSON.parse(content) as ProposedAction;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  proposedActions: [
                    ...(target.proposedActions ?? []).filter(
                      p => p.proposalId !== proposal.proposalId,
                    ),
                    { ...proposal, status: proposal.status ?? 'PENDING' },
                  ],
                }
              : msg,
          );
        });
        return;
      }

      // Must early-return like FILE_PRESENTED: the content is a JSON payload for the email
      // draft card, and falling through would render it as answer text. Deduping by draftId
      // keeps replayed/rejoined streams from double-rendering a card.
      if (state === ChatState.EMAIL_DRAFTED) {
        let draft: EmailDraft;
        try {
          draft = JSON.parse(content) as EmailDraft;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  emailDrafts: [
                    ...(target.emailDrafts ?? []).filter(d => d.draftId !== draft.draftId),
                    draft,
                  ],
                }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.OUTLOOK_CONNECT_REQUIRED) {
        let request: OutlookConnectRequest;
        try {
          request = JSON.parse(content) as OutlookConnectRequest;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  outlookConnectRequests: [
                    ...(target.outlookConnectRequests ?? []).filter(
                      r => r.requestId !== request.requestId,
                    ),
                    request,
                  ],
                }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.TOOL_LIMIT_REACHED) {
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  currentState: ChatState.TOOL_LIMIT_REACHED,
                  currentToolName: undefined,
                }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.TOOL_STARTED || state === ChatState.TOOL_FINISHED) {
        updateChatState(
          state === ChatState.TOOL_STARTED ? ChatState.THINKING : ChatState.ANSWERING,
        );
        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage) return currentMessages;
          const label = state === ChatState.TOOL_STARTED ? `${content}...` : '';
          const updatedMessage = {
            ...targetMessage,
            currentToolName: label,
            ...(state === ChatState.TOOL_FINISHED
              ? { restartCheckpointIndex: targetMessage.response.length }
              : {}),
          };
          return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
        });
        return;
      }

      if (state === ChatState.RESTARTED) {
        updateChatState(ChatState.THINKING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.THINKING);
        }
        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage) return currentMessages;
          const keptResponse = targetMessage.response.slice(
            0,
            targetMessage.restartCheckpointIndex ?? 0,
          );
          // The empty THINKING entry keeps the loading animation alive until the
          // restarted call streams its first chunk.
          keptResponse.push({ state: ChatState.THINKING, content: '' });
          const { thinkingContent, answerContent, currentState } =
            computeMessageProperties(keptResponse);
          const resetMessage: ChatMessage = {
            ...targetMessage,
            response: keptResponse,
            thinkingContent,
            answerContent,
            currentState,
            retryNotice: undefined,
            currentToolName: undefined,
          };
          return currentMessages.map(msg => (msg.id === messageId ? resetMessage : msg));
        });
        return;
      }

      if (state === ChatState.THINKING && chatStateRef.current !== ChatState.THINKING) {
        updateChatState(ChatState.THINKING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.THINKING);
        }
      } else if (state === ChatState.ANSWERING && chatStateRef.current !== ChatState.ANSWERING) {
        updateChatState(ChatState.ANSWERING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.ANSWERING);
        }
      }

      setMessages(currentMessages => {
        const targetMessage = findChatMessageById(currentMessages, messageId);
        if (!targetMessage) {
          const newMessage = createResponseMessage();
          newMessage.id = messageId;
          const updatedMessage = appendToResponse(newMessage, state, content);
          setLatestIncomingId(messageId);
          return [...currentMessages, updatedMessage];
        }

        const updatedMessage = appendToResponse(targetMessage, state, content);
        return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
      });
    },
    [updateChatState, queryClient],
  );

  const removeEmptyPlaceholderAndReset = useCallback(() => {
    setLatestIncomingId(null);
    setMessages(prev => prev.filter(msg => !isEmptyThinkingPlaceholder(msg)));
    if (modeConfigRef.current.fireTransitionCallbacks) {
      onStateChangeRef.current?.(ChatState.CANCELLED);
    }
    updateChatState(null);
  }, [updateChatState]);

  const runStream = useCallback(
    async (
      invokeStream: (
        onChunk: (rawData: string) => void,
        onError: () => void,
        onReconnecting: (reconnecting: boolean) => void,
      ) => Promise<StreamOutcome>,
      receivedMessageId: string,
      streamOptions: { markGiveUpOnEmptyEnd?: boolean; retryPayload?: RetryPayload } = {},
    ): Promise<void> => {
      const outcome = await invokeStream(
        rawData => handleIncomingStreamChunk(receivedMessageId, rawData),
        () => {
          removeEmptyPlaceholderAndReset();
          setMessages(prev => [
            ...prev,
            { kind: 'error', id: uuidv4(), retryPayload: streamOptions.retryPayload },
          ]);
        },
        reconnecting => setIsReconnecting(reconnecting),
      );

      setIsReconnecting(false);
      // A turn can create, edit or delete a firm skill through the skill manager, and the
      // composer's slash-command list is cached for minutes, so refresh it once the turn is over
      // rather than leaving the new skill missing from the next message.
      queryClient.invalidateQueries({ queryKey: CHAT_SKILLS_QUERY_KEY });

      // Switching to another conversation aborts this run. Since the chat hook is shared across
      // conversations, resetting here would wipe the state of whichever conversation is now open
      // (dropping its live chunks via the chatState===null guard); the aborting caller already
      // reset what it needed to.
      if (outcome.aborted) return;

      setMessages(prev => {
        const target = findChatMessageById(prev, receivedMessageId);
        const placeholderEmpty = target !== undefined && isEmptyThinkingPlaceholder(target);
        const withoutPlaceholders = prev.filter(msg => !isEmptyThinkingPlaceholder(msg));

        // The stream stopped without the terminator and could not be resumed. Keep whatever
        // streamed in but mark that it did not finish, so a dropped answer is never shown as
        // complete; a placeholder that never received any answer collapses to a failed marker.
        if (outcome.interrupted) {
          const marker: Message = placeholderEmpty
            ? { kind: 'failed', id: uuidv4() }
            : { kind: 'interrupted', id: uuidv4() };
          return [...withoutPlaceholders, marker];
        }

        // The stream ended on its own. If the turn's placeholder never received an answer, its
        // background worker never returned (a crash, or the tail gave up on a turn the stale-turn
        // sweep will finalise): leave a marker so the question does not look silently unanswered.
        // An errored run has already replaced the placeholder with an error message, so the target
        // is no longer an empty placeholder here and no marker is added.
        const gaveUp = streamOptions.markGiveUpOnEmptyEnd === true && placeholderEmpty;
        if (!gaveUp) return withoutPlaceholders;
        // Rejoining this turn again would replay the same silence, so drop it and leave a resend.
        const payload = streamOptions.retryPayload;
        return payload === undefined
          ? [...withoutPlaceholders, { kind: 'failed', id: uuidv4() }]
          : [
              ...withoutPlaceholders,
              { kind: 'error', id: uuidv4(), retryPayload: { ...payload, turnId: undefined } },
            ];
      });
      updateChatState(null);
      activeTurnRef.current = null;
    },
    [handleIncomingStreamChunk, updateChatState, removeEmptyPlaceholderAndReset, queryClient],
  );

  const fetchAndHandleStreamResponse = useCallback(
    async (
      url: string,
      body: object,
      receivedMessageId: string,
      conversationId: string,
      retryPayload?: RetryPayload,
    ): Promise<void> => {
      await runStream(
        (onChunk, onError) => streamRequest(url, body, onChunk, onError),
        receivedMessageId,
        { retryPayload },
      );
      // The live stream has finished in the open chat, so the user has now seen the answer.
      markConversationRead(conversationId);
    },
    [runStream, streamRequest, markConversationRead],
  );

  const streamExistingTurn = useCallback(
    async (
      conversationId: string,
      turnId: string,
      receivedMessageId: string,
      retryPayload?: RetryPayload,
    ): Promise<void> => {
      await runStream(
        (onChunk, onError, onReconnecting) =>
          subscribeToTurn(
            buildTurnStreamUrl(conversationId, turnId),
            onChunk,
            onError,
            onReconnecting,
          ),
        receivedMessageId,
        { markGiveUpOnEmptyEnd: true, retryPayload },
      );
      // The turn has finished streaming into the open chat, so the user has now seen it.
      markConversationRead(conversationId);
    },
    [runStream, subscribeToTurn, buildTurnStreamUrl, markConversationRead],
  );

  const generateInBackground = useCallback(
    async (
      content: string,
      clientId: string | null,
      conversationId: string,
      messageId: string,
      attachmentIds: string[],
      retryPayload: RetryPayload,
      familyId: string | null = null,
      notificationId?: string,
      skillName?: string,
    ): Promise<void> => {
      let turnId: string;
      try {
        const { data } = await api.post<{ turnId: string }>(
          `conversations/${conversationId}/turns`,
          {
            message: content,
            clientId: clientId ?? undefined,
            familyId: familyId ?? undefined,
            attachmentIds,
            notificationId,
            skillName,
          },
        );
        turnId = data.turnId;
      } catch (error) {
        // Enqueue rejected (e.g. the async-generation flag is off server-side): fall back to
        // the live streaming path so the user still gets a response.
        Sentry.captureException(error);
        if (familyId !== null) {
          await fetchAndHandleStreamResponse(
            buildFamilyChatUrl(familyId),
            { conversationId, message: content, attachmentIds, focusedClientId: null, skillName },
            messageId,
            conversationId,
            retryPayload,
          );
          return;
        }
        const url = clientId ? buildClientChatUrl(clientId) : buildCrossClientChatUrl();
        await fetchAndHandleStreamResponse(
          url,
          { conversationId, message: content, attachmentIds, notificationId, skillName },
          messageId,
          conversationId,
          retryPayload,
        );
        return;
      }

      activeTurnRef.current = { conversationId, turnId };
      await streamExistingTurn(conversationId, turnId, messageId, { ...retryPayload, turnId });
    },
    [
      streamExistingTurn,
      fetchAndHandleStreamResponse,
      buildClientChatUrl,
      buildCrossClientChatUrl,
      buildFamilyChatUrl,
    ],
  );

  const resumeActiveTurn = useCallback(
    (conversationId: string, turnId: string, retryPayload?: RetryPayload): void => {
      if (chatStateRef.current !== null) return;

      updateChatState(ChatState.THINKING);
      if (modeConfigRef.current.fireTransitionCallbacks) {
        onStateChangeRef.current?.(ChatState.THINKING);
      }

      const messageId = uuidv4();
      addMessage(createThinkingPlaceholderMessage(messageId));
      activeTurnRef.current = { conversationId, turnId };
      void streamExistingTurn(conversationId, turnId, messageId, retryPayload);
    },
    [streamExistingTurn, updateChatState, addMessage],
  );

  const sendUserMessage = useCallback(
    async (
      content: string,
      clientId: string | null,
      conversationId: string,
      attachedFiles: ChatFile[] = [],
      verify?: { sectionPath?: string[] },
      silent = false,
      // TODO: these trailing optional args are getting unwieldy; fold into a single options object.
      notificationId?: string,
      skill?: MessageSkill,
    ): Promise<void> => {
      if (chatStateRef.current !== null) {
        Sentry.addBreadcrumb({
          category: 'siesta',
          level: 'warning',
          message: 'sendUserMessage dropped: stream already in flight',
          data: { chatState: chatStateRef.current },
        });
        return;
      }

      const retryPayload: RetryPayload = {
        content,
        clientId,
        conversationId,
        attachedFiles,
        verify,
        silent,
        skill,
      };
      const skillName = skill?.name;

      updateChatState(ChatState.THINKING);
      if (modeConfigRef.current.fireTransitionCallbacks) {
        onStateChangeRef.current?.(ChatState.THINKING);
      }

      const attachments = attachedFiles.map(file => ({
        filename: file.filename,
        extension: file.extension,
      }));
      const attachmentIds = attachedFiles.map(file => file.id);

      const messageId = uuidv4();
      // System-initiated sends (verify, and the verify hand-off) are silent — the instruction
      // isn't something the user typed, so only the assistant's reply and thinking state show.
      if (!verify && !silent) {
        addMessage(createUserMessage(content, attachments, skill));
      }
      addMessage(createThinkingPlaceholderMessage(messageId));

      try {
        if (mode === ChatMode.DOCUMENT) {
          const docOptions = options as DocumentChatOptions;
          if (verify) verifyMeetingNoteIdRef.current = docOptions.entityId;
          const body = docOptions.requestBodyBuilder(content, conversationId, verify);
          await fetchAndHandleStreamResponse(
            buildDocumentEditUrl(docOptions.entityId),
            body,
            messageId,
            conversationId,
            retryPayload,
          );
          return;
        }

        if (mode === ChatMode.FAMILY) {
          const familyOptions = options as FamilyChatOptions;
          if (backgroundGenerationEnabled) {
            await generateInBackground(
              content,
              familyOptions.focusedClientId ?? null,
              conversationId,
              messageId,
              attachmentIds,
              retryPayload,
              familyOptions.focusedClientId ? null : familyOptions.familyId,
              notificationId,
              skillName,
            );
            return;
          }
          await fetchAndHandleStreamResponse(
            buildFamilyChatUrl(familyOptions.familyId),
            {
              conversationId,
              message: content,
              attachmentIds,
              notificationId,
              focusedClientId: familyOptions.focusedClientId ?? null,
              skillName,
            },
            messageId,
            conversationId,
            retryPayload,
          );
          return;
        }

        if (backgroundGenerationEnabled) {
          await generateInBackground(
            content,
            clientId,
            conversationId,
            messageId,
            attachmentIds,
            retryPayload,
            null,
            notificationId,
            skillName,
          );
          return;
        }

        const url = clientId ? buildClientChatUrl(clientId) : buildCrossClientChatUrl();
        await fetchAndHandleStreamResponse(
          url,
          { conversationId, message: content, attachmentIds, notificationId, skillName },
          messageId,
          conversationId,
          retryPayload,
        );
      } catch (error) {
        Sentry.captureException(error);
        removeEmptyPlaceholderAndReset();
      }
    },
    [
      mode,
      options,
      backgroundGenerationEnabled,
      generateInBackground,
      fetchAndHandleStreamResponse,
      addMessage,
      buildClientChatUrl,
      buildCrossClientChatUrl,
      buildFamilyChatUrl,
      buildDocumentEditUrl,
      updateChatState,
      removeEmptyPlaceholderAndReset,
    ],
  );

  const retryErroredSend = useCallback(
    async (marker: ErrorMarker): Promise<void> => {
      const payload = marker.retryPayload;
      // Guarded before the marker is dropped, so a retry refused mid-stream keeps its payload.
      if (!payload || chatStateRef.current !== null) return;
      const isTrailing = messages[messages.length - 1]?.id === marker.id;
      setMessages(prev => prev.filter(msg => msg.id !== marker.id));

      // Losing the subscription does not stop the worker, so rejoin the turn (replayed from its
      // first event) rather than enqueueing a second one for a question already being answered.
      if (payload.turnId) {
        resumeActiveTurn(payload.conversationId, payload.turnId, payload);
        return;
      }

      // Replaying silently relies on the question still being the last thing in the thread.
      await sendUserMessage(
        payload.content,
        payload.clientId,
        payload.conversationId,
        payload.attachedFiles,
        payload.verify,
        payload.silent || isTrailing,
        undefined,
        payload.skill,
      );
    },
    [messages, sendUserMessage, resumeActiveTurn],
  );

  const cancelStreaming = useCallback(() => {
    abortTransport();
    setIsReconnecting(false);
    removeEmptyPlaceholderAndReset();
    activeTurnRef.current = null;
  }, [abortTransport, removeEmptyPlaceholderAndReset]);

  // The explicit stop button: tell the worker to halt this turn's generation, then drop the
  // local subscription. The partial answer already streamed in stays on screen.
  const stopGeneration = useCallback(() => {
    const active = activeTurnRef.current;
    if (active) {
      void cancelTurn(active.conversationId, active.turnId);
    }
    cancelStreaming();
  }, [cancelTurn, cancelStreaming]);

  const clearMessages = useCallback(() => {
    cancelStreaming();
    setMessages([]);
  }, [cancelStreaming]);

  const loadHistory = useCallback(
    (rawMessages: StoredMessage[]) => {
      abortTransport();
      setIsReconnecting(false);
      updateChatState(null);
      setLatestIncomingId(null);
      setMessages(rawMessages.flatMap(rawMessageToChatMessages));
      setIsLoadingHistory(false);
    },
    [abortTransport, updateChatState],
  );

  const beginHistoryLoad = useCallback(() => setIsLoadingHistory(true), []);

  const endHistoryLoad = useCallback(() => setIsLoadingHistory(false), []);

  const replaceLastAssistantMessage = useCallback((content: string) => {
    setMessages(prev => {
      const lastIncomingIdx = [...prev]
        .reverse()
        .findIndex(m => isChatMessage(m) && m.direction === MessageDirection.Incoming);
      if (lastIncomingIdx === -1) return prev;
      const idx = prev.length - 1 - lastIncomingIdx;
      const replacement = createResponseMessage(content, prev[idx].id);
      return [...prev.slice(0, idx), replacement, ...prev.slice(idx + 1)];
    });
  }, []);

  const appendInterruptedMarker = useCallback(() => {
    setMessages(prev => [...prev, { kind: 'interrupted', id: uuidv4() }]);
  }, []);

  // Mark a reopened conversation whose latest turn was finalised as failed, so the trailing
  // question does not appear silently unanswered.
  const appendFailedMarker = useCallback(() => {
    setMessages(prev => [...prev, { kind: 'failed', id: uuidv4() }]);
  }, []);

  const markProposalDecided = useCallback(
    (
      messageId: string,
      proposalId: string,
      status: ProposedActionStatus,
      failureReason?: string | null,
    ) => {
      setMessages(prev =>
        prev.map(msg => {
          if (msg.id !== messageId || !isChatMessage(msg) || !msg.proposedActions) return msg;
          return {
            ...msg,
            proposedActions: msg.proposedActions.map(proposal =>
              proposal.proposalId === proposalId
                ? { ...proposal, status, failureReason: failureReason ?? proposal.failureReason }
                : proposal,
            ),
          };
        }),
      );
    },
    [],
  );

  // Sending a new message supersedes any still-pending proposal server-side at turn start;
  // mirror that here so an already-rendered widget never keeps live approve/deny buttons
  // stuck at the bottom of the chat.
  const supersedePendingProposals = useCallback(() => {
    setMessages(prev =>
      prev.map(msg => {
        if (!isChatMessage(msg) || !msg.proposedActions?.length) return msg;
        const isPending = (status?: ProposedActionStatus) => !status || status === 'PENDING';
        if (!msg.proposedActions.some(proposal => isPending(proposal.status))) return msg;
        return {
          ...msg,
          proposedActions: msg.proposedActions.map(proposal =>
            isPending(proposal.status) ? { ...proposal, status: 'SUPERSEDED' as const } : proposal,
          ),
        };
      }),
    );
  }, []);

  const finaliseLimitReachedMessage = useCallback((id: string) => {
    setMessages(prev =>
      prev.flatMap(msg => {
        if (msg.id !== id || !isChatMessage(msg)) return [msg];
        const hasPrePauseContent = Boolean(msg.answerContent.trim() || msg.thinkingContent.trim());
        if (!hasPrePauseContent) return [];
        return [{ ...msg, currentState: ChatState.ANSWERING }];
      }),
    );
  }, []);

  return {
    messages,
    sendUserMessage,
    retryErroredSend,
    resumeActiveTurn,
    latestIncomingId,
    clearMessages,
    loadHistory,
    isLoadingHistory,
    beginHistoryLoad,
    endHistoryLoad,
    replaceLastAssistantMessage,
    appendInterruptedMarker,
    appendFailedMarker,
    finaliseLimitReachedMessage,
    markProposalDecided,
    supersedePendingProposals,
    currentChatState,
    isReconnecting,
    cancelStreaming,
    stopGeneration,
  };
}
